// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Scaling helpers: zoomToScale, getBranchScale, getStepScale.
// All three are pure functions taking explicit arguments.

import { Angles, TreeTypes } from "./constants.js";

export function zoomToScale(zoom) {
  return Math.pow(2, zoom);
}

// Returns the pixel-per-unit-length ratio for branches.
// graph must have gone through applyTreeLayout so graph.width / graph.height
// are available (they reflect the centred bx/by bounding box * 2).
export function getBranchScale(graph, treeType, drawingArea, branchZoom) {
  let branchRatio;

  if (graph.root.totalSubtreeLength > 0) {
    switch (treeType) {
      case TreeTypes.Diagonal:
      case TreeTypes.Rectangular:
        branchRatio = drawingArea.width / graph.root.totalSubtreeLength;
        break;

      case TreeTypes.Hierarchical:
        branchRatio = drawingArea.height / graph.root.totalSubtreeLength;
        break;

      case TreeTypes.Circular:
      case TreeTypes.Radial: {
        const xAspectRatio = drawingArea.width / graph.width;
        const yAspectRatio = drawingArea.height / graph.height;
        branchRatio =
          xAspectRatio > yAspectRatio
            ? drawingArea.height / graph.height
            : drawingArea.width / graph.width;
        break;
      }

      default:
        throw new Error(`getBranchScale: unrecognised tree type "${treeType}"`);
    }
  } else {
    // Cladogram fallback: derive ratio from visible-leaf count
    switch (treeType) {
      case TreeTypes.Diagonal:
      case TreeTypes.Rectangular:
        branchRatio = drawingArea.width / (graph.root.visibleLeaves || 1);
        break;
      case TreeTypes.Hierarchical:
        branchRatio = drawingArea.height / (graph.root.visibleLeaves || 1);
        break;
      case TreeTypes.Circular:
      case TreeTypes.Radial: {
        const side = Math.min(drawingArea.width, drawingArea.height);
        branchRatio = side / ((graph.root.visibleLeaves || 1) * 2);
        break;
      }
      default:
        throw new Error(`getBranchScale: unrecognised tree type "${treeType}"`);
    }
  }

  return branchRatio * zoomToScale(branchZoom);
}

// Returns the pixel-per-step ratio used to space leaves along the cross-axis.
export function getStepScale(graph, treeType, drawingArea, stepZoom) {
  let stepRatio;

  switch (treeType) {
    case TreeTypes.Rectangular:
    case TreeTypes.Diagonal:
      stepRatio = drawingArea.height / (graph.root.visibleLeaves || 1);
      break;

    case TreeTypes.Hierarchical:
      stepRatio = drawingArea.width / (graph.root.visibleLeaves || 1);
      break;

    case TreeTypes.Circular:
    case TreeTypes.Radial:
      stepRatio = Angles.Degrees360 / (graph.root.visibleLeaves || 1);
      break;

    default:
      throw new Error(`getStepScale: unrecognised tree type "${treeType}"`);
  }

  return stepRatio * zoomToScale(stepZoom);
}
